import React, { useState, useEffect } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { SVG_ICONS } from "./TablerIconPicker";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const SharingReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shares, setShares] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [dayFilter, setDayFilter] = useState(7);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [summary, setSummary] = useState({
    totalShares: 0,
    activeShares: 0,
    expiredShares: 0,
    expiringSoon: 0,
    activeUsers: 0
  });
  const [chartData, setChartData] = useState([]);
  const [displayedCount, setDisplayedCount] = useState(10);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadSharingData();
  }, [dayFilter]);

  useEffect(() => {
    // Recalculate summary when statusFilter changes or shares update
    if (shares.length === 0) return;

    const nowDate = new Date();
    const daysAgo = new Date(nowDate.getTime() - dayFilter * 24 * 60 * 60 * 1000);

    const filteredShares = shares.filter(share => {
      if (!share.created_at) return true;
      const createdDate = new Date(share.created_at);
      return createdDate >= daysAgo;
    });

    const today = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
    const threeDaysLater = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);
    const now = new Date();

    let totalShares = filteredShares.length;
    let activeShares = 0;
    let expiredShares = 0;
    let expiringSoon = 0;
    const activeUsersSet = new Set();

    filteredShares.forEach(share => {
      // Apply status filter to summary
      if (statusFilter === "expired" && share.status !== "expired") return;
      if (statusFilter === "active" && share.status === "expired") return;

      // Track active users
      const isActive = share.status !== "expired" && (!share.expires_at || new Date(share.expires_at) > now);
      if (isActive && share.created_by_email) {
        activeUsersSet.add(share.created_by_email);
      }

      // Check status first
      if (share.status === "expired") {
        expiredShares++;
      } else if (share.expires_at) {
        const expiryDate = new Date(share.expires_at);
        const expiryDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

        if (expiryDate < now) {
          expiredShares++;
        } else if (expiryDay.getTime() <= threeDaysLater.getTime() && expiryDay.getTime() >= today.getTime()) {
          expiringSoon++;
          activeShares++;
        } else if (expiryDate > now) {
          activeShares++;
        }
      } else {
        activeShares++;
      }
    });

    // Adjust total when status filter is applied
    if (statusFilter === "expired") {
      totalShares = expiredShares;
    } else if (statusFilter === "active") {
      totalShares = activeShares + expiringSoon;
    }

    setSummary({
      totalShares,
      activeShares,
      expiredShares,
      expiringSoon,
      activeUsers: activeUsersSet.size
    });

    // Calculate weekly data by content type
    const weeklyData = {};
    filteredShares.forEach(share => {
      if (!share.created_at) return;
      const shareDate = new Date(share.created_at);
      const dayOfWeek = shareDate.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const weekStart = new Date(shareDate);
      weekStart.setDate(shareDate.getDate() - daysToSubtract);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const weekKey = weekEnd.toLocaleDateString();

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { lessons: 0, resources: 0, prompts: 0, news: 0 };
      }

      const contentType = (share.content_type || "").toLowerCase();
      if (contentType.includes("lesson")) {
        weeklyData[weekKey].lessons++;
      } else if (contentType.includes("resource")) {
        weeklyData[weekKey].resources++;
      } else if (contentType.includes("prompt")) {
        weeklyData[weekKey].prompts++;
      } else if (contentType.includes("news")) {
        weeklyData[weekKey].news++;
      }
    });

    const sortedWeeks = Object.entries(weeklyData)
      .map(([week, data]) => ({ week, ...data }))
      .sort((a, b) => new Date(a.week) - new Date(b.week));

    setChartData(sortedWeeks);
  }, [shares, dayFilter, statusFilter]);

  const handleViewLink = (share) => {
    if (share.short_url) {
      window.open(share.short_url, '_blank');
    }
  };

  const handleExpireLink = async (share) => {
    try {
      const scrollPos = window.scrollY;
      const now = new Date().toISOString();
      await sbFetch("PATCH", `shares?id=eq.${share.id}`, {
        status: "expired",
        expired_at: now
      });

      // Update just this row locally instead of reloading everything
      setShares(prevShares =>
        prevShares.map(s =>
          s.id === share.id
            ? { ...s, status: "expired", expired_at: now }
            : s
        )
      );
      setOpenMenuId(null);
      window.scrollTo(0, scrollPos);
    } catch (err) {
      console.error("Error expiring link:", err);
    }
  };

  const handleDeleteShare = async (share) => {
    try {
      const scrollPos = window.scrollY;
      await sbFetch("DELETE", `shares?id=eq.${share.id}`);

      // Remove from local state
      setShares(prevShares => prevShares.filter(s => s.id !== share.id));
      setOpenMenuId(null);
      window.scrollTo(0, scrollPos);
    } catch (err) {
      console.error("Error deleting share:", err);
    }
  };

  const toggleSelectShare = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = (filteredShares) => {
    if (selectedIds.size === filteredShares.length && filteredShares.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredShares.map(s => s.id)));
    }
  };

  const handleBulkExpire = async () => {
    try {
      const scrollPos = window.scrollY;
      const now = new Date().toISOString();
      for (const id of selectedIds) {
        await sbFetch("PATCH", `shares?id=eq.${id}`, {
          status: "expired",
          expired_at: now
        });
      }

      // Update local state
      setShares(prevShares =>
        prevShares.map(s =>
          selectedIds.has(s.id)
            ? { ...s, status: "expired", expired_at: now }
            : s
        )
      );
      setSelectedIds(new Set());
      window.scrollTo(0, scrollPos);
    } catch (err) {
      console.error("Error bulk expiring shares:", err);
    }
  };

  const handleBulkDelete = async () => {
    try {
      const scrollPos = window.scrollY;
      for (const id of selectedIds) {
        await sbFetch("DELETE", `shares?id=eq.${id}`);
      }

      // Remove from local state
      setShares(prevShares => prevShares.filter(s => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
      window.scrollTo(0, scrollPos);
    } catch (err) {
      console.error("Error bulk deleting shares:", err);
    }
  };

  const loadSharingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch shares
      const sharesData = await sbFetch("GET", "shares?select=*").catch(() => []);

      if (Array.isArray(sharesData)) {
        // Auto-expire shares where expires_at has passed but not yet marked as expired
        const now = new Date().toISOString();
        const sharesToUpdate = sharesData.filter(share =>
          share.status !== 'expired' &&
          share.expires_at &&
          new Date(share.expires_at) < new Date()
        );

        if (sharesToUpdate.length > 0) {
          sharesToUpdate.forEach(share => {
            sbFetch("PATCH", `shares?id=eq.${share.id}`, {
              status: "expired",
              expired_at: share.expires_at
            }).catch(err => console.error("Error auto-expiring share:", err));
          });
        }

        setShares(sharesData);

        // Filter shares by day range
        const nowDate = new Date();
        const daysAgo = new Date(nowDate.getTime() - dayFilter * 24 * 60 * 60 * 1000);

        const filteredShares = sharesData.filter(share => {
          if (!share.created_at) return true;
          const createdDate = new Date(share.created_at);
          return createdDate >= daysAgo;
        });

        // Calculate summary metrics based on filtered shares
        const today = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
        const threeDaysLater = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);

        let totalShares = filteredShares.length;
        let activeShares = 0;
        let expiredShares = 0;
        let expiringSoon = 0;

        filteredShares.forEach(share => {
          // Check status first - if manually expired, it's expired regardless of date
          if (share.status === "expired") {
            expiredShares++;
          } else if (share.expires_at) {
            const expiryDate = new Date(share.expires_at);
            const expiryDay = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());

            if (expiryDate < now) {
              expiredShares++;
            } else if (expiryDay.getTime() <= threeDaysLater.getTime() && expiryDay.getTime() >= today.getTime()) {
              expiringSoon++;
              activeShares++;
            } else if (expiryDate > now) {
              activeShares++;
            }
          } else {
            activeShares++;
          }
        });

        setSummary({
          totalShares,
          activeShares,
          expiredShares,
          expiringSoon
        });
      }
    } catch (err) {
      console.error("Error loading sharing data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ color: "#7A9CC4", fontSize: "14px" }}>Loading sharing data...</div>;
  }

  if (error) {
    return <div style={{ color: "#EF4444", fontSize: "14px" }}>Error loading data: {error}</div>;
  }

  return (
    <div>
      {/* Day Filter Buttons */}
      <div style={{
        marginBottom: "20px"
      }}>
        <div style={{ color: "#7A9CC4", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.5px" }}>Date Range</div>
        <div style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap"
        }}>
          {[7, 30, 60, 90].map((days) => (
          <button
            key={days}
            onClick={() => setDayFilter(days)}
            style={{
              padding: "8px 16px",
              background: dayFilter === days ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.1)",
              border: dayFilter === days ? "1px solid rgba(59, 130, 246, 0.5)" : "1px solid rgba(59, 130, 246, 0.2)",
              borderRadius: "6px",
              color: dayFilter === days ? "#5B9EF0" : "#7A9CC4",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(59, 130, 246, 0.2)";
              e.target.style.borderColor = "rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = dayFilter === days ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.1)";
              e.target.style.borderColor = dayFilter === days ? "rgba(59, 130, 246, 0.5)" : "rgba(59, 130, 246, 0.2)";
            }}
          >
            {days} Days
          </button>
          ))}
        </div>
      </div>

      {/* Status Filter Buttons */}
      <div style={{
        marginBottom: "24px"
      }}>
        <div style={{ color: "#7A9CC4", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.5px" }}>Status</div>
        <div style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap"
        }}>
          {[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "expired", label: "Expired" }
          ].map((status) => (
          <button
            key={status.value}
            onClick={() => setStatusFilter(status.value)}
            style={{
              padding: "8px 16px",
              background: statusFilter === status.value ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.1)",
              border: statusFilter === status.value ? "1px solid rgba(16, 185, 129, 0.5)" : "1px solid rgba(16, 185, 129, 0.2)",
              borderRadius: "6px",
              color: statusFilter === status.value ? "#10B981" : "#7A9CC4",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(16, 185, 129, 0.2)";
              e.target.style.borderColor = "rgba(16, 185, 129, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = statusFilter === status.value ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.1)";
              e.target.style.borderColor = statusFilter === status.value ? "rgba(16, 185, 129, 0.5)" : "rgba(16, 185, 129, 0.2)";
            }}
          >
            {status.label}
          </button>
          ))}
        </div>
      </div>

      {/* Page Size Selector */}
      <div style={{
        marginBottom: "24px"
      }}>
        <div style={{ color: "#7A9CC4", fontSize: "12px", fontWeight: "600", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.5px" }}>Records Per Page</div>
        <div style={{
          display: "flex",
          gap: "8px"
        }}>
          {[10, 20, 50, 100].map((size) => (
            <button
              key={size}
              onClick={() => {
                setPageSize(size);
                setDisplayedCount(size);
              }}
              style={{
                padding: "8px 12px",
                background: pageSize === size ? "rgba(168, 85, 247, 0.3)" : "rgba(168, 85, 247, 0.1)",
                border: pageSize === size ? "1px solid rgba(168, 85, 247, 0.5)" : "1px solid rgba(168, 85, 247, 0.2)",
                borderRadius: "6px",
                color: pageSize === size ? "#A855F7" : "#7A9CC4",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(168, 85, 247, 0.2)";
                e.target.style.borderColor = "rgba(168, 85, 247, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = pageSize === size ? "rgba(168, 85, 247, 0.3)" : "rgba(168, 85, 247, 0.1)";
                e.target.style.borderColor = pageSize === size ? "rgba(168, 85, 247, 0.5)" : "rgba(168, 85, 247, 0.2)";
              }}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "16px",
        marginBottom: "32px"
      }}>
        {/* Total Shares */}
        <div style={{
          padding: "24px",
          background: "rgba(59, 130, 246, 0.08)",
          border: "1px solid rgba(59, 130, 246, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Total Shares</h3>
          <div style={{ color: "#3B82F6", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{summary.totalShares}</div>
          <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>All shares</p>
        </div>

        {/* Active Shares */}
        <div style={{
          padding: "24px",
          background: "rgba(16, 185, 129, 0.08)",
          border: "1px solid rgba(16, 185, 129, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Active Shares</h3>
          <div style={{ color: "#10B981", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{summary.activeShares}</div>
          <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Currently valid</p>
        </div>

        {/* Expired Shares */}
        <div style={{
          padding: "24px",
          background: "rgba(239, 68, 68, 0.08)",
          border: "1px solid rgba(239, 68, 68, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Expired</h3>
          <div style={{ color: "#EF4444", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{summary.expiredShares}</div>
          <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Expired links</p>
        </div>

        {/* Expiring Soon */}
        <div style={{
          padding: "24px",
          background: "rgba(251, 146, 60, 0.08)",
          border: "1px solid rgba(251, 146, 60, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Expiring Soon</h3>
          <div style={{ color: "#FB923C", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{summary.expiringSoon}</div>
          <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Within 3 days</p>
        </div>

        {/* Active Users Sharing */}
        <div style={{
          padding: "24px",
          background: "rgba(168, 85, 247, 0.08)",
          border: "1px solid rgba(168, 85, 247, 0.2)",
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <h3 style={{ color: "#7A9CC4", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", margin: "0 0 12px 0", letterSpacing: "0.5px" }}>Active Users Sharing</h3>
          <div style={{ color: "#A855F7", fontSize: "32px", fontWeight: "700", marginBottom: "8px" }}>{summary.activeUsers}</div>
          <p style={{ color: "#9DB4D4", fontSize: "12px", margin: "0" }}>Users with active shares</p>
        </div>
      </div>

      {/* Bulk Actions */}
      <div style={{
        display: "flex",
        gap: "12px",
        marginBottom: "24px",
        flexWrap: "wrap"
      }}>
        {selectedIds.size > 0 && (
          <div style={{
            display: "flex",
            gap: "8px",
            padding: "8px 12px",
            background: "rgba(59, 130, 246, 0.1)",
            borderRadius: "6px",
            alignItems: "center"
          }}>
            <span style={{ color: "#7A9CC4", fontSize: "13px" }}>{selectedIds.size} selected</span>
            <button
              type="button"
              onClick={handleBulkExpire}
              style={{
                padding: "6px 12px",
                background: "rgba(251, 146, 60, 0.2)",
                border: "1px solid rgba(251, 146, 60, 0.3)",
                borderRadius: "4px",
                color: "#FB923C",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              Expire
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              style={{
                padding: "6px 12px",
                background: "rgba(239, 68, 68, 0.2)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "4px",
                color: "#EF4444",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Shares by Week Chart */}
      {chartData.length > 0 && (
        <div style={{
          background: "rgba(59, 130, 246, 0.05)",
          border: "1px solid rgba(59, 130, 246, 0.15)",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "32px"
        }}>
          <h3 style={{ color: "#5B9EF0", margin: "0 0 20px 0" }}>Shares by Week and Content Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="rgba(59, 130, 246, 0.2)" />
              <XAxis
                dataKey="week"
                stroke="#7A9CC4"
                tick={{ fontSize: 12 }}
              />
              <YAxis stroke="#7A9CC4" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: "rgba(5, 14, 26, 0.9)", border: "1px solid rgba(59, 130, 246, 0.3)" }}
                labelStyle={{ color: "#5B9EF0" }}
              />
              <Legend
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="lessons"
                stroke="#34D399"
                strokeWidth={2}
                dot={{ fill: "#34D399", r: 4 }}
                activeDot={{ r: 6 }}
                name="Lessons"
              />
              <Line
                type="monotone"
                dataKey="resources"
                stroke="#F59E0B"
                strokeWidth={2}
                dot={{ fill: "#F59E0B", r: 4 }}
                activeDot={{ r: 6 }}
                name="Resources"
              />
              <Line
                type="monotone"
                dataKey="prompts"
                stroke="#A855F7"
                strokeWidth={2}
                dot={{ fill: "#A855F7", r: 4 }}
                activeDot={{ r: 6 }}
                name="Prompts"
              />
              <Line
                type="monotone"
                dataKey="news"
                stroke="#EC4899"
                strokeWidth={2}
                dot={{ fill: "#EC4899", r: 4 }}
                activeDot={{ r: 6 }}
                name="News"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Shares Table */}
      {(() => {
        const now = new Date();
        const daysAgo = new Date(now.getTime() - dayFilter * 24 * 60 * 60 * 1000);
        const filteredShares = shares.filter(share => {
          // Apply day filter
          if (!share.created_at) return true;
          const createdDate = new Date(share.created_at);
          if (createdDate < daysAgo) return false;

          // Apply status filter
          if (statusFilter === "active" && share.status === "expired") return false;
          if (statusFilter === "expired" && share.status !== "expired") return false;

          return true;
        });

        return filteredShares.length > 0 ? (
        <>
          <div style={{
            background: "rgba(15, 23, 42, 0.4)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "8px",
            overflowX: "auto"
          }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px"
            }}>
            <thead>
              <tr style={{ background: "rgba(59, 130, 246, 0.1)", borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
                <th style={{ padding: "12px", textAlign: "center", width: "40px" }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredShares.length && filteredShares.length > 0}
                    onChange={() => toggleSelectAll(filteredShares)}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Resource</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Shared By</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Short URL</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Created</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Expires</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Expired At</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "center", color: "#7A9CC4", fontWeight: "600", width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredShares.slice(0, Math.ceil(displayedCount / pageSize) * pageSize).map((share, idx) => {
                const isExpired = share.status === "expired" || (share.expires_at && new Date(share.expires_at) < now);
                const expiryDate = share.expires_at ? new Date(share.expires_at).toLocaleDateString() : "Never";
                const expiredDate = share.expired_at ? new Date(share.expired_at).toLocaleString() : "—";

                let resourceLabel = "N/A";
                if (share.content_type) {
                  resourceLabel = share.content_type;
                  if (share.content_data) {
                    try {
                      const data = typeof share.content_data === "string" ? JSON.parse(share.content_data) : share.content_data;
                      if (data.title) resourceLabel += ` - ${data.title}`;
                    } catch (e) {
                      // If JSON parse fails, just use content_type
                    }
                  }
                }

                return (
                  <tr key={idx} style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.1)" }}>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(share.id)}
                        onChange={() => toggleSelectShare(share.id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ padding: "12px", color: "#9DB4D4", maxWidth: "300px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{resourceLabel}</td>
                    <td style={{ padding: "12px", color: "#9DB4D4" }}>{share.created_by_email || "N/A"}</td>
                    <td style={{ padding: "12px", color: "#9DB4D4" }}>
                      {share.short_url ? (
                        <a
                          href={share.short_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#5B9EF0",
                            textDecoration: "none",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontFamily: "monospace",
                            padding: "4px 8px",
                            background: "rgba(59, 130, 246, 0.1)",
                            borderRadius: "4px",
                            display: "inline-block",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "rgba(59, 130, 246, 0.2)";
                            e.target.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "rgba(59, 130, 246, 0.1)";
                            e.target.style.textDecoration = "none";
                          }}
                        >
                          {share.short_url}
                        </a>
                      ) : (
                        <span>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: "12px", color: "#9DB4D4" }}>
                      {share.created_at ? new Date(share.created_at).toLocaleDateString() : "N/A"}
                    </td>
                    <td style={{ padding: "12px", color: "#9DB4D4" }}>{expiryDate}</td>
                    <td style={{ padding: "12px", color: share.expired_at ? "#FCA5A5" : "#7A9CC4", display: "flex", alignItems: "center", gap: "6px" }}>
                      {share.expired_at && SVG_ICONS["hourglass"]({ size: 14, color: share.expired_at ? "#FCA5A5" : "#7A9CC4" })}
                      {expiredDate}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "600",
                        background: isExpired ? "rgba(239, 68, 68, 0.2)" : "rgba(16, 185, 129, 0.2)",
                        color: isExpired ? "#FCA5A5" : "#86EFAC"
                      }}>
                        {isExpired ? "Expired" : "Active"}
                      </span>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center", position: "relative" }}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === idx ? null : idx)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#7A9CC4",
                          fontSize: "20px",
                          cursor: "pointer",
                          padding: "4px 8px"
                        }}
                      >
                        ⋮
                      </button>
                      {openMenuId === idx && (
                        <div style={{
                          position: "absolute",
                          right: "0",
                          top: "100%",
                          background: "rgba(15, 23, 42, 0.95)",
                          border: "1px solid rgba(59, 130, 246, 0.3)",
                          borderRadius: "6px",
                          minWidth: "150px",
                          zIndex: 1000,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                        }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLink(share);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "10px 12px",
                              background: "none",
                              border: "none",
                              color: "#5B9EF0",
                              fontSize: "13px",
                              cursor: "pointer",
                              borderBottom: "1px solid rgba(59, 130, 246, 0.2)"
                            }}
                          >
                            View Link
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExpireLink(share);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "10px 12px",
                              background: "none",
                              border: "none",
                              color: "#EF4444",
                              fontSize: "13px",
                              cursor: "pointer",
                              borderBottom: "1px solid rgba(59, 130, 246, 0.2)"
                            }}
                          >
                            Expire Link
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteShare(share);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "10px 12px",
                              background: "none",
                              border: "none",
                              color: "#EF4444",
                              fontSize: "13px",
                              cursor: "pointer"
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          {Math.ceil(displayedCount / pageSize) * pageSize < filteredShares.length && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                type="button"
                onClick={() => setDisplayedCount(displayedCount + pageSize)}
                style={{
                  padding: "10px 20px",
                  background: "rgba(59, 130, 246, 0.2)",
                  border: "1px solid rgba(59, 130, 246, 0.4)",
                  borderRadius: "6px",
                  color: "#5B9EF0",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.2)";
                }}
              >
                Load More
              </button>
            </div>
          )}
        </>
      ) : (
        <div style={{
          padding: "32px",
          textAlign: "center",
          color: "#7A9CC4",
          background: "rgba(59, 130, 246, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(59, 130, 246, 0.2)"
        }}>
          <p>No shares found</p>
        </div>
      );
      })()}
    </div>
  );
};

export default SharingReport;
